import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

function Section({ n, title, children }) {
  return (
    <section className="mt-10" data-testid={`privacy-section-${n}`}>
      <h2 className="font-serif text-2xl lg:text-3xl text-[#F5F4F0] leading-tight">
        {n}. {title}
      </h2>
      <div className="mt-4 space-y-4 text-[#8A99AA] leading-relaxed">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page" className="bg-navy pt-40 pb-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <Link to="/" data-testid="privacy-back" className="inline-flex items-center gap-2 label-overline">
          <ArrowLeft size={14} /> Back home
        </Link>

        <header className="mt-8">
          <div className="label-overline">Legal</div>
          <h1 className="mt-3 text-5xl lg:text-6xl text-[#F5F4F0] leading-[1.05] font-serif">
            Privacy Notice
          </h1>
          <p className="mt-6 text-[#8A99AA] leading-relaxed">
            Gurukul of Ramana (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;)<br />
            Website:{" "}
            <a href="https://gurukuloframana.com" className="text-[#F5F4F0] underline decoration-[#C6A87C] underline-offset-2">
              https://gurukuloframana.com
            </a>
            <br />
            Last updated: July 23, 2026
          </p>
          <p className="mt-5 text-[#8A99AA] leading-relaxed">
            Gurukul of Ramana is committed to protecting the privacy of everyone who
            visits our website. This Privacy Notice explains what personal data we
            collect, why we collect it, how we use and protect it, and the rights you
            have in relation to it — whether you are visiting from India or from
            anywhere else in the world.
          </p>
          <p className="mt-4 text-[#8A99AA] leading-relaxed">
            This notice is designed to meet the requirements of India&rsquo;s Digital
            Personal Data Protection Act, 2023 (DPDP Act) and Information Technology
            Rules, and to align with the principles of the EU/UK General Data
            Protection Regulation (GDPR) and the California Consumer Privacy Act
            (CCPA/CPRA) for visitors and clients outside India.
          </p>
        </header>

        <div className="hairline-divider my-10" />

        <Section n={1} title="Who We Are">
          <p>
            Gurukul of Ramana (&ldquo;Data Fiduciary&rdquo; under the DPDP Act, &ldquo;Data
            Controller&rdquo; under GDPR) is the entity responsible for deciding how and
            why your personal data is processed through this website. Any questions or
            requests relating to this notice should be sent to the contact details in
            Section 11.
          </p>
        </Section>

        <Section n={2} title="What Personal Data We Collect">
          <p>
            We collect personal data only when you voluntarily provide it to us —
            currently, this happens through our contact/inquiry form. When you submit
            an inquiry through our website, we may collect:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number (if provided)</li>
            <li>Message/inquiry details you choose to share with us</li>
          </ul>
          <p>
            We use cookies and third-party analytics and advertising technologies on
            this website to understand how visitors use the site and to improve our
            services and marketing. These currently include Google Analytics and Google
            Tag Manager (usage analytics), and AdRoll and Shown.io (advertising and
            retargeting). These tools may set cookies or similar identifiers and may
            collect information such as pages visited, time on site, approximate
            location, device and browser type, and interactions with our contact
            buttons. You can control non-essential cookies through the cookie consent
            banner shown on your first visit, and through your browser settings. We do
            not collect payment information through this website.
          </p>
          <p>
            We may also automatically receive limited technical information (such as
            your IP address and browser type) through our web hosting provider&rsquo;s
            standard server logs, purely for security and website functioning purposes.
          </p>
        </Section>

        <Section n={3} title="Why We Collect Your Data (Purpose of Processing)">
          <p>We use the personal data submitted through our contact form solely to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Respond to your inquiry, question, or request for information</li>
            <li>Communicate with you about the services, programs, or content you asked about</li>
            <li>Maintain a record of correspondence for reference and quality purposes</li>
          </ul>
          <p>
            We do not use your data for automated decision-making, profiling, or any
            purpose unrelated to responding to your inquiry.
          </p>
        </Section>

        <Section n={4} title="Legal Basis for Processing">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-[#F5F4F0]">Under the DPDP Act (India):</strong> We
              process your data based on your consent, which you give by voluntarily
              filling out and submitting the contact form.
            </li>
            <li>
              <strong className="text-[#F5F4F0]">Under GDPR (EU/UK visitors):</strong> Our
              legal basis is your consent (Art. 6(1)(a)) and, where applicable, our
              legitimate interest in responding to inquiries you initiate (Art.
              6(1)(f)).
            </li>
            <li>
              <strong className="text-[#F5F4F0]">Under CCPA/CPRA (California visitors):</strong>{" "}
              We do not sell or share your personal information with third parties for
              monetary or other valuable consideration.
            </li>
          </ul>
        </Section>

        <Section n={5} title="How We Share Your Data">
          <p>
            We do not sell, rent, or trade your personal data. We may share your data
            only with:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Service providers who help us operate our website or email systems (e.g.,
              hosting or email delivery providers), under confidentiality obligations,
              and only to the extent needed to provide their service
            </li>
            <li>
              Legal authorities, if required to comply with a valid legal obligation,
              court order, or government request
            </li>
            <li>
              Successors, in the event of a merger, acquisition, or sale of assets —
              with prior notice to you where required by law
            </li>
          </ul>
          <p>
            We do not transfer your data to any third party for marketing or
            advertising purposes.
          </p>
        </Section>

        <Section n={6} title="International Data Transfers">
          <p>
            Because we welcome inquiries from visitors around the world, your data may
            be processed on servers located outside your home country, including in
            India. Where we transfer personal data internationally, we take reasonable
            steps to ensure it receives an adequate level of protection consistent with
            the DPDP Act, GDPR, and other applicable laws, including through contractual
            safeguards with our service providers where required.
          </p>
        </Section>

        <Section n={7} title="Data Retention">
          <p>
            We retain personal data submitted via the contact form only for as long as
            necessary to respond to your inquiry and maintain reasonable business
            records, after which it is securely deleted or anonymized, unless a longer
            retention period is required by law.
          </p>
        </Section>

        <Section n={8} title="Data Security">
          <p>
            We implement reasonable technical and organizational measures — such as
            secure transmission (HTTPS/SSL) and restricted access to inquiry data — to
            protect your personal data against unauthorized access, alteration,
            disclosure, or destruction. However, no method of transmission or storage
            is 100% secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section n={9} title="Your Privacy Rights">
          <p>
            Depending on where you are located, you may have some or all of the
            following rights:
          </p>
          <p className="font-semibold text-[#F5F4F0]">If you are in India (DPDP Act, 2023):</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Right to access information about your personal data and its processing</li>
            <li>Right to correction, completion, and updating of your personal data</li>
            <li>Right to erasure of your personal data once it is no longer needed for the purpose collected</li>
            <li>Right to withdraw your consent at any time, as easily as it was given</li>
            <li>Right to nominate another individual to exercise these rights on your behalf in the event of death or incapacity</li>
            <li>Right to grievance redressal (see Section 10)</li>
          </ul>
          <p className="font-semibold text-[#F5F4F0]">If you are in the EU/UK (GDPR):</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Right to access, rectify, or erase your personal data</li>
            <li>Right to restrict or object to processing</li>
            <li>Right to data portability</li>
            <li>Right to withdraw consent at any time</li>
            <li>Right to lodge a complaint with your local data protection authority</li>
          </ul>
          <p className="font-semibold text-[#F5F4F0]">If you are in California (CCPA/CPRA):</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Right to know what personal information we collect and how it is used</li>
            <li>Right to request deletion of your personal information</li>
            <li>Right to opt out of sale/sharing (note: we do not sell or share your data)</li>
            <li>Right to non-discrimination for exercising your privacy rights</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the details in
            Section 11. We will respond to verified requests within the timeframe
            required by applicable law.
          </p>
        </Section>

        <Section n={10} title="Grievance Redressal">
          <p>
            If you have any concerns about how your personal data has been handled,
            please contact us first at the details below so we can address your concern
            directly. In accordance with the DPDP Act and Information Technology Rules,
            you may also escalate unresolved grievances to the Data Protection Board of
            India, once constituted, or the relevant supervisory authority in your
            jurisdiction.
          </p>
        </Section>

        <Section n={11} title="Contact Us">
          <p>
            For any questions, requests, or concerns about this Privacy Notice or your
            personal data, please contact:
          </p>
          <div className="bg-navy-deep border border-[#C6A87C]/20 p-6">
            <div className="font-serif text-xl text-[#F5F4F0]">Gurukul of Ramana</div>
            <div className="mt-2 text-sm space-y-1">
              <div>
                Website:{" "}
                <a href="https://gurukuloframana.com" className="text-[#F5F4F0] underline decoration-[#C6A87C] underline-offset-2">
                  https://gurukuloframana.com
                </a>
              </div>
              <div>
                <a href="mailto:venkatvasu@hotmail.com" className="text-[#F5F4F0] underline decoration-[#C6A87C] underline-offset-2">
                  venkatvasu@hotmail.com
                </a>
              </div>
              <div>
                <a href="tel:+919819998036" className="text-[#F5F4F0] underline decoration-[#C6A87C] underline-offset-2">
                  +91-9819998036
                </a>
              </div>
            </div>
          </div>
        </Section>

        <Section n={12} title="Children's Privacy">
          <p>
            Our website is not directed at children under the age of 18 (or the
            applicable age of consent in your jurisdiction), and we do not knowingly
            collect personal data from children. If we learn that a child&rsquo;s data has
            been submitted without appropriate consent, we will delete it promptly.
          </p>
        </Section>

        <Section n={13} title="Changes to This Notice">
          <p>
            We may update this Privacy Notice from time to time to reflect changes in
            our practices, our website, or applicable law. The &ldquo;Last updated&rdquo; date
            at the top of this page indicates when it was last revised. We encourage
            you to review this notice periodically.
          </p>
        </Section>
      </div>
    </div>
  );
}
